import { StructuredDataSchema } from '@repo/shared/dtos/structured-data'
import { DataProvider } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@repo/ui/tabs'
import { cn } from '@repo/ui/utils'
import { delay } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { useAbility } from '@/ability/ability-context'
import { BeneficiaryDocuments } from '@/components/beneficiary/BeneficiaryDocuments'
import { BeneficiaryEditor } from '@/components/beneficiary/BeneficiaryEditor'
import { BeneficiaryNotes } from '@/components/beneficiary/BeneficiaryNotes'
import { BeneficiaryStructuredData } from '@/components/beneficiary/BeneficiaryStructuredData'
import { ReportsTrigger } from '@/components/beneficiary/report/Reports'
import { SkeletonCard } from '@/components/skeletons/SkeletonCard'
import { SkeletonNote } from '@/components/skeletons/SkeletonNote'
import { useView } from '@/hooks'
import { isRoute, RouteNames } from '@/routes'
import { BeneficiaryChatRoute } from '@/routes/beneficiary/BeneficiaryChatRoute'
import { BeneficiaryTasksRoute } from '@/routes/beneficiary/BeneficiaryTasksRoute'
import { AvailableParams } from '@/routes/router'
import { RootState } from '@/store'
import { storeActions } from '@/store/actions'
import { clear } from '@/store/case-workers/slice'
import { getCaseWorkers } from '@/store/case-workers/thunks'
import { useFeatureFlags } from '@/store/feature-flags/hooks'
import { getCurrentBeneficiary, getCurrentOrganization } from '@/store/helpers'
import { useStoreDispatch } from '@/store/hooks'
import { selectTasksCount } from '@/store/task'

export function BeneficiaryRoute() {
  const { hasFeature } = useFeatureFlags()
  const hasChatFeature = hasFeature('chat')
  return (
    <DataProvider
      selectors={[getCurrentOrganization]}
      render={(organization) => (
        <>
          <div className="flex flex-1 flex-col space-y-4 overflow-hidden overflow-y-auto px-4 pt-4 xl:space-y-6">
            <BeneficiaryTabs
              structuredDataSchema={organization.structuredDataSchema}
              hasChatFeature={hasChatFeature}
            />
          </div>
        </>
      )}
    />
  )
}

enum TabNames {
  Chat = 'chat',
  Documents = 'documents',
  Notes = 'notes',
  StructuredDataSchema = 'structuredData',
  Tasks = 'tasks',
}

export function BeneficiaryTabs({
  hasChatFeature,
  structuredDataSchema,
}: {
  hasChatFeature: boolean
  structuredDataSchema: StructuredDataSchema
}) {
  const dispatch = useStoreDispatch()
  const { tc } = useT()
  const ability = useAbility()

  let { chatSessionId } = useParams<AvailableParams>()
  const location = useLocation()
  const pathname = useMemo(() => location.pathname, [location.pathname])

  const hasStructuredData = structuredDataSchema.length > 0
  const defaultValue = TabNames.Notes

  const [tab, setTab] = useState<TabNames>(defaultValue)

  const view = useView()
  const handleTabChange = async (value: TabNames) => {
    // Navigate to the corresponding route
    if (!hasChatFeature && value === TabNames.Tasks) view.beneficiary().tasks.navigate()
    if (hasChatFeature && value === TabNames.Chat) {
      if (!chatSessionId) {
        const res = await dispatch(
          storeActions.beneficiary.chatSession.selectLastChatSession(),
        ).unwrap()
        chatSessionId = res.chatSessionId
      }
      view.beneficiary().chat(chatSessionId).navigate()
    } else view.beneficiary().profile.navigate()
    delay(() => setTab(value), 100)
  }
  useEffect(() => {
    if (isRoute(RouteNames.Beneficiaries, pathname)) {
      setTab(defaultValue)
    } else if (isRoute(RouteNames.BeneficiaryTasks, pathname) && !hasChatFeature) {
      setTab(TabNames.Tasks)
    } else if ((isRoute(RouteNames.BeneficiaryChat, pathname) || chatSessionId) && hasChatFeature) {
      setTab(TabNames.Chat)
    }
  }, [pathname, defaultValue, chatSessionId])

  const handleOpen = (open: boolean) => {
    if (ability.can('accessRoute', 'admin')) {
      if (open) {
        void dispatch(getCaseWorkers())
      } else void dispatch(clear())
    }
  }
  return (
    <Tabs
      defaultValue={defaultValue}
      value={tab}
      onValueChange={(tab) => void handleTabChange(tab as TabNames)}
    >
      <div className="flex items-center justify-between pb-2">
        <TabsList className="px-2 py-6">
          <TabsTrigger value={TabNames.Notes} className="text-base">
            {tc('note', { count: 2, cfl: true })}
          </TabsTrigger>
          {!hasChatFeature && (
            <TabsTrigger value={TabNames.Tasks} className="text-base">
              <DataProvider
                loadingComponent={<SkeletonNote />}
                selectors={[selectTasksCount]}
                render={(count) => (
                  <>
                    {tc('tasks', { cfl: true })}
                    <Counter className="bg-primary-100" count={count} />
                  </>
                )}
              />
            </TabsTrigger>
          )}
          {hasStructuredData && (
            <TabsTrigger value={TabNames.StructuredDataSchema} className="text-base">
              {tc('situation', { cfl: true })}
            </TabsTrigger>
          )}
          <TabsTrigger value={TabNames.Documents} className="text-base">
            {tc('document', { count: 2, cfl: true })}
          </TabsTrigger>
          {hasChatFeature && (
            <TabsTrigger value={TabNames.Chat} className="text-base">
              {tc('chat', { cfl: true })}
            </TabsTrigger>
          )}
        </TabsList>
        <div className="flex items-center space-x-2">
          <DataProvider
            selectors={[getCurrentBeneficiary]}
            render={(beneficiary) => (
              <BeneficiaryEditor onOpen={handleOpen} beneficiary={beneficiary} />
            )}
          />
          <ReportsTrigger />
        </div>
      </div>

      {hasStructuredData && (
        <TabsContent value={TabNames.StructuredDataSchema}>
          <BeneficiaryStructuredData />
        </TabsContent>
      )}

      {hasChatFeature ? (
        <TabsContent value={TabNames.Chat}>
          <BeneficiaryChatRoute />
        </TabsContent>
      ) : (
        <TabsContent value={TabNames.Tasks}>
          <BeneficiaryTasksRoute />
        </TabsContent>
      )}

      <TabsContent value={TabNames.Notes}>
        <DataProvider
          placeholderComponent={<SkeletonCard className="mt-2" />}
          loadingComponent={<SkeletonCard className="mt-2" />}
          selectors={[(state: RootState) => state.document.all]}
          render={(allDocuments) => <BeneficiaryNotes allDocuments={allDocuments} />}
        />
      </TabsContent>

      <TabsContent value={TabNames.Documents}>
        <DataProvider
          placeholderComponent={<SkeletonCard className="mt-2" />}
          loadingComponent={<SkeletonCard className="mt-2" />}
          selectors={[(state: RootState) => state.document.all]}
          render={(allDocuments) => <BeneficiaryDocuments allDocuments={allDocuments} />}
        />
      </TabsContent>
    </Tabs>
  )
}

export function Counter({ count, className }: { count: number; className?: string }) {
  return (
    <div
      className={cn(
        'bg-primary-600 flex size-6 items-center justify-center rounded-full font-mono text-xs',
        className,
      )}
    >
      {count}
    </div>
  )
}
